import { IMetricResult, NotFoundError, TimeSpan } from 'fitbeat.models';
import { getWorkoutDuration } from '../../config/homeworkoutdurations';
import { LeaderboardConfig } from '../../config/leaderboardconfig';
import { WorkoutType } from '../../config/workouttype';
import { CardioOrResistance } from '../../entities/exercise/exercise';
import { UserProfile } from '../../entities/user';
import { UserWorkoutSession } from '../../entities/workoutsession/userworkoutsession';
import { sleep } from '../../helpers';
import { calculateMetrics } from '../../metrics';
import {
  IHomeGroupClassRepository,
  IUserProfileRepository,
  IWorkoutHeartRateDataRepository,
  IWorkoutRepository,
} from '../../persistence';
import { IBookingsManager } from '../bookings';
import { getInitials } from '../challenge';
import { HeartRateData } from '../performancemetrics';
import { PseudoRandomGenerator } from '../workoutselection/pseudorandomgenerator';
import { HomeGroupClass, IPointInTimeInfo } from './homegroupclass';

enum dividerType {
  fullrow = 'fullrow',
  elipsis = 'elipsis',
}

export interface ILeaderboardRow {
  position?: number; // '-' if undefined
  initials: string;
  name: string;
  effortPoints: number | string;
  dividerType: dividerType;
  lastActiveSecond?: string;
  isMine: boolean;
  cardioOrResistance: CardioOrResistance;
  workoutId: string;
}

export interface ILeaderboardReponse {
  leaderboardRows: ILeaderboardRow[];
  groupCallLeaderboardRows: { [key: string]: ILeaderboardRow };
}

interface IWorkoutsByWorkoutType {
  [key: number]: UserWorkoutSession[];
}

interface IHomeGroupClassByWorkoutType {
  [key: string]: HomeGroupClass;
}

export type TemplateLeaderboard = IHomeGroupClassByWorkoutType;

interface IWorkoutSettingsCache {
  pairedWorkoutId: string;
  userProfileId: string;
  cardioOrResistance: CardioOrResistance;
  workoutType: WorkoutType;
  fullname: string;
  initial: string;
  workoutStartTime: Date;
  effortPoints: number | string;
}

interface ISettingsCacheByWorkoutId {
  [key: string]: IWorkoutSettingsCache;
}

export class LeaderboardManager {
  public static generateFakeNamesForWorkoutIds(
    workoutsByWorkoutType: UserWorkoutSession[],
    longListOfRandomFirstnames: string[],
    shuffleSeed: number,
  )
    : Map<string, { name: string, initial: string }> {
    const allFakeNamesShuffled = PseudoRandomGenerator
      .shuffleArrayPseudoRandomly(longListOfRandomFirstnames, shuffleSeed);

    const fakeNamesForAmountOfWorkouts = allFakeNamesShuffled.slice(0, workoutsByWorkoutType.length);

    const fakeNamesForWorkoutIds = new Map<string, { name: string, initial: string }>();
    for (const workout of workoutsByWorkoutType) {
      const name = fakeNamesForAmountOfWorkouts.pop();
      if (name != null) {
        const initial = getInitials(name);
        fakeNamesForWorkoutIds.set(workout.uniqueReference, { name, initial });
      }
    }
    return fakeNamesForWorkoutIds;
  }

  public static buildLeaderboardRows(
    workoutId: string,
    leaderboardRows: ILeaderboardRow[],
    maxAmountToShow: number,
  ): ILeaderboardRow[] {
    if (!leaderboardRows || leaderboardRows.length === 0) {
      return [];
    }

    const usersPosition = leaderboardRows.findIndex((row) => row.workoutId === workoutId);

    if (leaderboardRows[usersPosition]) {
      leaderboardRows[usersPosition].isMine = true;
    }

    if (leaderboardRows.length <= maxAmountToShow) {
      return leaderboardRows;
    }

    const {
      startOfFirstSection,
      getStartOfLastSection,
      getEndOfLastSection,
    } = LeaderboardConfig;
    const startOfLastSection = getStartOfLastSection(leaderboardRows);
    const endOfLastSection = getEndOfLastSection(leaderboardRows);

    const isInFirstSection = usersPosition < maxAmountToShow;
    const isLast = usersPosition === leaderboardRows.length - 1;

    if (isInFirstSection) {
      return this.buildLeaderboardInFirstSection(leaderboardRows, startOfFirstSection, maxAmountToShow);
    }
    if (isLast) {
      return this.buildLeaderboardInLastSection(leaderboardRows, startOfLastSection, endOfLastSection, maxAmountToShow);
    }
    return this.buildLeaderBoardInMiddleSection(leaderboardRows, usersPosition, maxAmountToShow);
  }

  private static buildLeaderboardInFirstSection(
    leaderboard: ILeaderboardRow[],
    upperLimit: number,
    lowerLimit: number,
  ): ILeaderboardRow[] {
    const firstSection = leaderboard.slice(upperLimit, lowerLimit);
    firstSection[firstSection.length - 1].dividerType = dividerType.elipsis;
    return firstSection;
  }

  private static buildLeaderboardInLastSection(
    leaderboard: ILeaderboardRow[],
    upperLimit: number,
    lowerLimit: number,
    maxAmountToShow: number,
  ): ILeaderboardRow[] {
    const lastSection = leaderboard.slice(upperLimit, lowerLimit);
    const amountLeftToDisplay = maxAmountToShow - lastSection.length;
    const firstSection = leaderboard.slice(0, amountLeftToDisplay);
    firstSection[firstSection.length - 1].dividerType = dividerType.elipsis;
    return [...firstSection, ...lastSection];
  }

  private static buildLeaderBoardInMiddleSection(
    leaderboard: ILeaderboardRow[],
    usersPosition: number,
    maxAmountToShow: number,
  ): ILeaderboardRow[] {
    const positionAboveUser = usersPosition - 1;
    const positionBelowUser = usersPosition + 2;
    const positionsAroundUser = leaderboard.slice(positionAboveUser, positionBelowUser);
    const lastItem = positionsAroundUser[positionsAroundUser.length - 1];
    if (lastItem !== leaderboard[leaderboard.length - 1]) {
      lastItem.dividerType = dividerType.elipsis;
    }

    const amountLeftToDisplay = maxAmountToShow - positionsAroundUser.length;
    const firstSection = leaderboard.slice(0, amountLeftToDisplay);
    if (firstSection.length > 0) {
      firstSection[firstSection.length - 1].dividerType = dividerType.elipsis;
    }
    return [...firstSection, ...positionsAroundUser];
  }

  private cachedMappedHrData = new Map<string, HeartRateData>();

  private workoutSettingsCache: ISettingsCacheByWorkoutId = {};

  constructor(
    private workoutDb: IWorkoutRepository,
    private workoutHeartRateDataDb: IWorkoutHeartRateDataRepository,
    private userProfileDb: IUserProfileRepository,
    private homeGroupClassDb: IHomeGroupClassRepository,
    private bookingsManager: IBookingsManager,
    private logger: any, // todo: add a logger with startTimer
    private longListOfRandomFirstnames: string[]
    = ['Alice', 'Bob', 'Charlie', 'Debby', 'Eva', 'Fane', 'Heidi', 'Ivan', 'Jack'],
  ) { }

  public async populateAndCacheTemplateHomeGroupClass(slowDownSyncOperations = false) {
    if (this.longListOfRandomFirstnames == null || this.longListOfRandomFirstnames.length < 1) {
      throw Error('No items fake first names list, aborting population leaderboard template home group class');
    }
    const startOf2021Year = TimeSpan.createStartOfDayDate(new Date('2021-01-01'));
    const workouts = await this.workoutDb.getWorkoutsForLeaderboard(startOf2021Year);
    const workoutIds = workouts.map(({ uniqueReference }) => uniqueReference);
    const heartRateDataPoints = await this.getHeartRatePoints(workoutIds);
    const workoutsWithHeartRatePoints = this.getWorkoutsWithHeartRatePoints(workouts, heartRateDataPoints);

    const workoutsByWorkoutType = this.sortAndGroupByWorkoutType(workoutsWithHeartRatePoints);

    const selectedWorkouts = this.splitAndSelectWorkouts(
      workoutsByWorkoutType,
      LeaderboardConfig.numberOfWorkoutsInHomeGroupClass,
      LeaderboardConfig.randomBucketSize,
    );

    const userProfileIds = this.getUserProfileIds(selectedWorkouts);
    const userProfiles = await this.userProfileDb.getUsersByIds(userProfileIds);
    const templateLeaderBoard = await this.buildTemplateLeaderboard(
      selectedWorkouts,
      userProfiles,
      heartRateDataPoints,
      CardioOrResistance.Cardio,
      slowDownSyncOperations,
    );

    await this.homeGroupClassDb.addTemplateLeaderboard(templateLeaderBoard);
  }

  public async updateHomeGroupClassAndGetLeaderboard(
    workoutId: string,
    metricResult: IMetricResult | undefined,
    timeLeftInWorkoutMs: number,
    dateOfRequest: Date = new Date(),
  ): Promise<ILeaderboardReponse> {
    const cachedGroupCallSettings = await this.getWorkoutSettingsAndBuildCache(workoutId, dateOfRequest);
    const cachedWorkoutSettings = cachedGroupCallSettings[workoutId];
    const {
      pairedWorkoutId, cardioOrResistance, workoutType, fullname, initial, userProfileId,
    } = cachedWorkoutSettings;

    let leaderboard = await this.homeGroupClassDb
      .getWorkoutLeaderboard(pairedWorkoutId, cardioOrResistance, workoutType);

    if (!leaderboard) {
      const templateLeaderboard = await this.generateTemplateLeaderboard(cardioOrResistance, workoutType, workoutId);
      leaderboard = await this.homeGroupClassDb
        .addWorkoutLeaderboard(pairedWorkoutId, cardioOrResistance, workoutType, templateLeaderboard);
    }

    const secondsInWorkout = this.getSecondsInWorkout(workoutType, timeLeftInWorkoutMs);
    const currentSecondInWorkout = this.getCurrentSecondInWorkout(leaderboard, secondsInWorkout);

    const usersPointInTime: IPointInTimeInfo = {
      effortPoints: this.getMetricLoad(metricResult),
      name: fullname,
      isBot: false,
      workoutId,
      initial,
      userProfileId,
      lastActiveSecond: currentSecondInWorkout.toString(),
      cardioOrResistance,
    };
    cachedGroupCallSettings[workoutId].effortPoints = usersPointInTime.effortPoints;

    // Removing from leaderboard so that has more live workouts thus improving the chances real workouts show on the UI
    const botWorkoutIdToRemove: string | undefined = this.getBotWorkoutIdToRemove(
      workoutId,
      userProfileId,
      leaderboard,
      currentSecondInWorkout,
      dateOfRequest.getDay(),
    );

    leaderboard = await this.homeGroupClassDb.updateWithUserInfoAtTime(
      usersPointInTime,
      currentSecondInWorkout,
      botWorkoutIdToRemove,
      pairedWorkoutId,
      cardioOrResistance,
      workoutType,
    );

    const leaderboardAtCurrentPointInTime = this.sortLeaderboardRowByEffortPoints(
      leaderboard.pointInTimeLeaderboard[currentSecondInWorkout],
    );

    return {
      leaderboardRows: leaderboardAtCurrentPointInTime,
      groupCallLeaderboardRows: this.buildGroupLeaderboardRows(cachedGroupCallSettings, pairedWorkoutId),
    };
  }

  public async clearOldWorkoutLeaderboards(date: Date): Promise<void> {
    await this.homeGroupClassDb.removeOldWorkoutLeaderboards(date);
    this.clearOldWorkoutSettingsCache(date);
  }

  private async generateTemplateLeaderboard(
    cardioOrResistance: CardioOrResistance,
    workoutType: WorkoutType,
    workoutId: string,
  ) {
    const workout = await this.workoutDb.getWorkoutSessionFromId(workoutId);
    if (!workout) {
      throw new Error(`workout not found for ${workoutId}`);
    }

    const templateLeaderboard = await this.homeGroupClassDb.getTemplateLeaderboard(workoutType);
    if (!templateLeaderboard) {
      throw new Error(`No leaderboard template for ${cardioOrResistance} and ${workoutType}`);
    }

    const shouldNotHaveBots = UserWorkoutSession.isHomeGroupBooking(workout);
    if (shouldNotHaveBots) {
      templateLeaderboard.removeAllBots();
    }
    return templateLeaderboard;
  }

  private clearOldWorkoutSettingsCache(date: Date): void {
    Object.keys(this.workoutSettingsCache).forEach((key) => {
      const settingsCache = this.workoutSettingsCache[key];
      const { workoutStartTime } = settingsCache;
      const lifespanMs = LeaderboardConfig.lifespanOfWorkoutLeaderboardHour * 60 * 60 * 1000;
      if ((date.getTime() - workoutStartTime.getTime()) > lifespanMs) {
        delete this.workoutSettingsCache[key];
      }
    });
  }

  private getSecondsInWorkout(workoutType: WorkoutType, timeLeftInWorkoutMs: number): number {
    const totalWorkoutDuration = getWorkoutDuration(workoutType).workoutDurationInMinutes * 60 * 1000;
    const secondsInWorkout = (totalWorkoutDuration - timeLeftInWorkoutMs) / 1000;
    return Math.max(0, secondsInWorkout);
  }

  private getCurrentSecondInWorkout(workoutLeaderboard: HomeGroupClass, secondsInWorkout: number): number {
    const leaderboardsMatchingSeconds = Object.keys(workoutLeaderboard.pointInTimeLeaderboard)
      .map((key) => parseInt(key, 10))
      .filter((second) => second <= secondsInWorkout);

    return leaderboardsMatchingSeconds[leaderboardsMatchingSeconds.length - 1];
  }

  private async getWorkoutSettingsAndBuildCache(
    workoutId: string,
    requestTime: Date,
  ): Promise<ISettingsCacheByWorkoutId> {
    const cachedWorkoutSettings = this.workoutSettingsCache;
    if (cachedWorkoutSettings[workoutId]) {
      return cachedWorkoutSettings;
    }

    const workout = await this.workoutDb.getWorkoutSessionFromId(workoutId);
    if (!workout) {
      throw NotFoundError.Workout(workoutId);
    }

    const userProfile = await this.userProfileDb.get(workout.userProfileId);
    if (!userProfile) {
      throw new NotFoundError('user', workout.userProfileId);
    }

    if (!UserWorkoutSession.isHomeGroupBooking(workout)) {
      this.workoutSettingsCache[workoutId] = {
        pairedWorkoutId: UserWorkoutSession.getOpenInviteReference(workout) || workoutId,
        userProfileId: userProfile.uid,
        cardioOrResistance: workout.settings.cardioOrResistance,
        workoutType: workout.workoutType,
        fullname: userProfile.fullname,
        initial: getInitials(userProfile.fullname),
        workoutStartTime: workout.startTimeOfFirstExercise,
        effortPoints: '-',
      };
    } else {
      const openInviteClass = await this.bookingsManager.GetOpenInviteClassWithWorkoutSettings(workout, requestTime);
      openInviteClass.forEach((participant) => {
        if (this.workoutSettingsCache[participant.workoutId]) {
          return;
        }

        this.workoutSettingsCache[participant.workoutId] = {
          pairedWorkoutId: UserWorkoutSession.getOpenInviteReference(workout) || workoutId,
          userProfileId: participant.userProfileId,
          cardioOrResistance: participant.cardioOrResistance,
          workoutType: participant.workoutType,
          fullname: participant.fullname,
          initial: getInitials(participant.fullname),
          workoutStartTime: participant.startTimeOfFirstExercise,
          effortPoints: '-',
        };
      });
    }

    return this.workoutSettingsCache;
  }

  private getBotWorkoutIdToRemove(
    workoutId: string,
    userProfileId: string,
    leaderboard: HomeGroupClass,
    currentSecondInWorkout: number,
    daySeed: number,
  ): string | undefined {
    let botWorkoutIdToRemove: string | undefined;

    const leaderboardAtPointInTime = leaderboard.pointInTimeLeaderboard[currentSecondInWorkout];
    const isLeaderboardFull = leaderboardAtPointInTime.length
      === LeaderboardConfig.numberOfWorkoutsInHomeGroupClass + 1;
    const workoutNotInLeaderboard = leaderboard.pointInTimeLeaderboard[currentSecondInWorkout]
      .findIndex((pointInTime) => pointInTime.workoutId === workoutId) === -1;
    const userProfileIdNotInLeaderboard = leaderboard.pointInTimeLeaderboard[currentSecondInWorkout]
      .findIndex((pointInTime) => pointInTime.userProfileId === userProfileId) === -1;
    if (isLeaderboardFull && workoutNotInLeaderboard && userProfileIdNotInLeaderboard) {
      const botWorkouts = leaderboardAtPointInTime.filter((pointInTimeInfo) => pointInTimeInfo.isBot);
      const leaderboardShuffled = PseudoRandomGenerator.shuffleArrayPseudoRandomly(botWorkouts, daySeed);
      if (leaderboardShuffled.length === 0) {
        return undefined;
      }
      botWorkoutIdToRemove = leaderboardShuffled[0].workoutId;
    }

    return botWorkoutIdToRemove;
  }

  private async buildTemplateLeaderboard(
    selectedWorkouts: IWorkoutsByWorkoutType,
    userProfiles: UserProfile[],
    heartRateDataPoints: HeartRateData[],
    cardioOrResistance: CardioOrResistance,
    slowDownSyncOperations: boolean,
  ) {
    const timer = this.logger.startTimer();
    let artificialDelays = 0;
    const homeGroupClassesByWorkoutType: IHomeGroupClassByWorkoutType = {};

    for (const workoutType of Object.keys(selectedWorkouts)) {
      const homeGroupClass = new HomeGroupClass();
      const workoutsByWorkoutType: UserWorkoutSession[] = selectedWorkouts[workoutType];
      const fakeNamesForWorkouts = LeaderboardManager
        .generateFakeNamesForWorkoutIds(workoutsByWorkoutType, this.longListOfRandomFirstnames, Date.now());

      const durationWorkoutS = getWorkoutDuration(workoutType as any).workoutDurationInMinutes * 60;

      for (let interval = 0; interval <= durationWorkoutS; interval += LeaderboardConfig.secondInterval) {
        workoutsByWorkoutType.forEach((workout) => {
          const userProfile = userProfiles.find(({ uid }) => uid === workout.userProfileId);
          const heartRateData = heartRateDataPoints.find(({ id }) => id === workout.uniqueReference);

          if (!heartRateData || !userProfile) {
            return;
          }

          const filteredHeartRateData = heartRateData.heartRateDataPoints.filter((point) => {
            const heartRateTimestampInWorkout = new Date(point.timestamp).getTime() / 1000;
            const inHrRange = heartRateTimestampInWorkout <= interval;
            return inHrRange;
          });

          const metrics = calculateMetrics(userProfile, filteredHeartRateData, cardioOrResistance);
          const load = this.getMetricLoad(metrics);
          let fakeName = fakeNamesForWorkouts.get(workout.uniqueReference);
          if (fakeName == null) {
            fakeName = { name: 'Anon', initial: 'AA' };
          }
          const pointInTimeData: IPointInTimeInfo = {
            effortPoints: load,
            name: fakeName.name,
            initial: fakeName.initial,
            workoutId: workout.uniqueReference,
            isBot: true,
            userProfileId: workout.userProfileId,
            cardioOrResistance,
          };
          homeGroupClass.addPointInTimeToLeaderboard(interval.toString(), pointInTimeData);
        });

        if (slowDownSyncOperations) {
          await sleep(50);
          artificialDelays += 1;
        }
      }

      homeGroupClassesByWorkoutType[workoutType] = homeGroupClass;
    }

    timer.done(`LeaderboardManager: buildTemplateLeaderboard (${artificialDelays} artificial delays)`);
    return homeGroupClassesByWorkoutType;
  }

  private getWorkoutsWithHeartRatePoints(
    workouts: UserWorkoutSession[],
    heartRateDataPoints: HeartRateData[],
  ): UserWorkoutSession[] {
    const timer = this.logger.startTimer();
    const result = workouts.filter((workout) => {
      const exists = heartRateDataPoints.find((heartRateData) => heartRateData.id === workout.uniqueReference);
      const durationOfWorkout = getWorkoutDuration(workout.workoutType).workoutDurationInMinutes * 60 * 1000;

      if (!exists) {
        return false;
      }

      let workoutHasEnoughDataPoints = false;
      const first = exists.heartRateDataPoints[0];
      const last = exists.heartRateDataPoints[exists.heartRateDataPoints.length - 1];
      const diff = last.timestamp.getTime() - first.timestamp.getTime();
      workoutHasEnoughDataPoints = diff > (durationOfWorkout * LeaderboardConfig.minPercentageOfRawPointsToDuration);

      return workoutHasEnoughDataPoints;
    });
    timer.done('LeaderboardManager: getWorkoutsWithHeartRatePoints');
    return result;
  }

  private splitAndSelectWorkouts(
    workouts: IWorkoutsByWorkoutType,
    numberOfWorkoutsToSelect: number,
    numberOfBuckets: number,
  ): IWorkoutsByWorkoutType {
    const selectedWorkouts: IWorkoutsByWorkoutType = {};

    for (const key of Object.keys(workouts)) {
      const workoutsForWorkoutType: UserWorkoutSession[] = workouts[key];
      const bucketSplitAmount = Math.ceil(workoutsForWorkoutType.length / numberOfBuckets);
      const workoutsSelected: UserWorkoutSession[] = [];

      let prev = 0;
      for (let i = 0; workoutsSelected.length < numberOfWorkoutsToSelect && workoutsForWorkoutType.length > 0; i++) {
        const bucket = workoutsForWorkoutType.slice(prev, prev + bucketSplitAmount);
        if (bucket.length === 0) {
          prev = 0;
          continue;
        }

        const randomSelectionIndex = this.randomSelectionIndex(bucket.length);
        // spread breaks the type
        // eslint-disable-next-line prefer-object-spread
        const selectedWorkout = Object.assign({}, bucket[randomSelectionIndex]);

        workoutsSelected.push(selectedWorkout);

        const workoutIndex = workoutsForWorkoutType
          .findIndex(({ uniqueReference }) => uniqueReference === selectedWorkout.uniqueReference);
        workoutsForWorkoutType.splice(workoutIndex, 1);
        bucket.splice(randomSelectionIndex, 1);
        prev += bucketSplitAmount;
      }

      selectedWorkouts[key] = workoutsSelected;
    }

    return selectedWorkouts;
  }

  private randomSelectionIndex(bucketLength: number) {
    return Math.floor(Math.random() * (bucketLength - 0));
  }

  private sortAndGroupByWorkoutType(workouts: UserWorkoutSession[]): { [key: number]: UserWorkoutSession[] } {
    const sortedByLoadWorkouts = this.sortByMetricLoad(workouts);
    const sortedAndGrouped = sortedByLoadWorkouts.reduce((result, current) => {
      if (result[current.workoutType]) {
        result[current.workoutType].push(current);
      } else {
        result[current.workoutType] = [current];
      }
      return result;
    }, {});
    return sortedAndGrouped;
  }

  private sortByMetricLoad(workouts: UserWorkoutSession[]): UserWorkoutSession[] {
    return workouts.sort((a, b) => {
      if (!a.performanceMetrics || !b.performanceMetrics) {
        return 0;
      }
      return a.performanceMetrics.load < b.performanceMetrics.load ? 1 : -1;
    });
  }

  private getUserProfileIds(workouts: IWorkoutsByWorkoutType): string[] {
    const userIds: string[] = [];
    for (const key of Object.keys(workouts)) {
      const workout: UserWorkoutSession[] = workouts[key];
      userIds.push(...workout.map(({ userProfileId }) => userProfileId));
    }

    return userIds;
  }

  private async getHeartRatePoints(workoutIds: string[]): Promise<HeartRateData[]> {
    const timer = this.logger.startTimer();
    const workoutIdsNotCached = workoutIds.filter((workoutId) => !this.cachedMappedHrData.has(workoutId));
    const newlyLoaded = await this.getHeartRatePointsFromDbAndMap(workoutIdsNotCached);
    for (const hrData of newlyLoaded) {
      // cache the mapped items, and not in the repo, since there is some sync work done
      // at the end of getHeartRatePointsFromDbAndMap that should also be cached
      this.cachedMappedHrData.set(hrData.id, hrData);
    }

    // add empty items for not found entries, to prevent trying to load from db
    const stillNotFound = workoutIds.filter((workoutId) => !this.cachedMappedHrData.has(workoutId));
    for (const workoutId of stillNotFound) {
      this.cachedMappedHrData.set(workoutId, new HeartRateData(workoutId, [[0, 0]]));
    }

    // we could filter and get the specific items from cache, but it likely will be most of them
    // the returned list is merged later on getWorkoutsWithHeartRatePoints
    const allCached = [...this.cachedMappedHrData.values()];
    timer.done('LeaderboardManager getHeartRatePoints');
    return allCached;
  }

  private async getHeartRatePointsFromDbAndMap(workoutIds: string[]): Promise<HeartRateData[]> {
    const timer = this.logger.startTimer();
    const rawDataPoints = await this.workoutHeartRateDataDb.getAll(workoutIds);

    const mapped = rawDataPoints.map(({ id, heartRateDataPoints }) => {
      const filtered = heartRateDataPoints.filter(({ timestamp }) => timestamp >= new Date(0));
      return {
        id,
        heartRateDataPoints: filtered,
      };
    });
    timer.done('LeaderboardManager: getHeartRatePointsFromDbAndMap');
    return mapped;
  }

  private sortLeaderboardRowByEffortPoints(leaderboardRows: IPointInTimeInfo[]): ILeaderboardRow[] {
    const emptyLoadPoints = leaderboardRows.filter(({ effortPoints }) => typeof effortPoints === 'string');
    const loadPoints = leaderboardRows.filter(({ effortPoints }) => typeof effortPoints === 'number');

    const sortedLeaderboardRows = loadPoints.sort((a, b) => {
      if (a.effortPoints < b.effortPoints) {
        return 1;
      }
      if (a.effortPoints > b.effortPoints) {
        return -1;
      }
      return 0;
    });

    return [...sortedLeaderboardRows, ...emptyLoadPoints].map((pointInTimeInfo, index) => ({
      initials: pointInTimeInfo.initial,
      name: pointInTimeInfo.name.substring(0, 10),
      effortPoints: pointInTimeInfo.effortPoints,
      position: index + 1,
      dividerType: dividerType.fullrow,
      lastActiveSecond: pointInTimeInfo.lastActiveSecond,
      isMine: false,
      workoutId: pointInTimeInfo.workoutId,
      cardioOrResistance: pointInTimeInfo.cardioOrResistance,
    }));
  }

  private getMetricLoad(metricResult?: IMetricResult): number | string {
    return metricResult && typeof metricResult.load !== 'undefined' ? metricResult.load : '-';
  }

  private getCachedGroupCallSettingsForClass(
    cachedGroupCallSettings: ISettingsCacheByWorkoutId,
    pairedWorkoutId: string,
  ): ISettingsCacheByWorkoutId {
    return Object.keys(cachedGroupCallSettings).reduce((result, key) => {
      const setting = cachedGroupCallSettings[key];
      if (pairedWorkoutId === setting.pairedWorkoutId) {
        result[key] = setting;
      }
      return result;
    }, {});
  }

  private buildGroupLeaderboardRows(
    cachedGroupCallSettings: ISettingsCacheByWorkoutId,
    pairedWorkoutId: string,
  ): { [key: string]: ILeaderboardRow } {
    const groupCallLeaderboardRows: { [key: string]: ILeaderboardRow } = {};
    const cacheForClassWorkout = this.getCachedGroupCallSettingsForClass(cachedGroupCallSettings, pairedWorkoutId);

    Object.keys(cacheForClassWorkout).forEach((key) => {
      const setting = cacheForClassWorkout[key];
      groupCallLeaderboardRows[setting.userProfileId] = {
        cardioOrResistance: setting.cardioOrResistance,
        effortPoints: setting.effortPoints,
        workoutId: key,
        name: setting.fullname,
        initials: setting.initial,
        dividerType: dividerType.fullrow,
        isMine: false,
      };
    });

    return groupCallLeaderboardRows;
  }
}
